import React, { useState, useRef, useEffect, useCallback } from 'react';
import './styles.css';

interface ExportActions {
  onExportExcel?: () => void;
  onExportCsv?: () => void;
  // 新增：控制导出按钮状态的属性
  hasData?: boolean; // 是否有数据
  dataCount?: number; // 数据条数（可选，用于显示）
}

interface ResizablePanelsProps {
  leftPanel: React.ReactNode;
  rightPanel: React.ReactNode;
  defaultLeftWidth?: number;
  minLeftWidth?: number;
  minRightWidth?: number;
  // 新增：导出功能相关props
  leftPanelExportActions?: ExportActions;
}

const ResizablePanels: React.FC<ResizablePanelsProps> = ({
  leftPanel,
  rightPanel,
  defaultLeftWidth = 70,
  minLeftWidth = 300,
  minRightWidth = 350,
  leftPanelExportActions, // 新增：导出功能
}) => {
  const [leftWidth, setLeftWidth] = useState(defaultLeftWidth);
  const [isDragging, setIsDragging] = useState(false);
  const [isLeftCollapsed, setIsLeftCollapsed] = useState(false);
  const [isRightCollapsed, setIsRightCollapsed] = useState(false);
  const [isExportMenuVisible, setIsExportMenuVisible] = useState(false); // 新增：控制导出菜单显示
  const containerRef = useRef<HTMLDivElement>(null);
  const dividerRef = useRef<HTMLDivElement>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null); // 新增：导出菜单ref
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);

  // 检查是否有数据
  const hasData = leftPanelExportActions?.hasData ?? true; // 默认为true保持向后兼容
  const dataCount = leftPanelExportActions?.dataCount ?? 0;

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    e.preventDefault();

    // 拖拽开始时禁用 transition
    if (leftPanelRef.current) {
      leftPanelRef.current.classList.add('no-transition');
    }
    if (rightPanelRef.current) {
      rightPanelRef.current.classList.add('no-transition');
    }
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !containerRef.current) return;

    // 使用 requestAnimationFrame 来节流更新
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const newLeftWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;

    // 计算最小宽度百分比
    const minLeftPercent = (minLeftWidth / containerRect.width) * 100;
    const minRightPercent = (minRightWidth / containerRect.width) * 100;

    if (newLeftWidth >= minLeftPercent && newLeftWidth <= 100 - minRightPercent) {
      // setLeftWidth(newLeftWidth);
      if (newLeftWidth >= minLeftPercent && newLeftWidth <= 100 - minRightPercent) {
        // 直接更新 DOM 样式，避免频繁的状态更新
        if (leftPanelRef.current && rightPanelRef.current) {
          leftPanelRef.current.style.width = `${newLeftWidth}%`;
          leftPanelRef.current.style.flex = `0 0 ${newLeftWidth}%`;
          rightPanelRef.current.style.width = `${100 - newLeftWidth}%`;
          rightPanelRef.current.style.flex = `0 0 ${100 - newLeftWidth}%`;
        }
      }
    }
  }, [isDragging, minLeftWidth, minRightWidth]);

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // 取消待处理的动画帧
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // 获取最终位置并更新状态
    if (containerRef.current && leftPanelRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const leftPanelRect = leftPanelRef.current.getBoundingClientRect();
      const finalLeftWidth = (leftPanelRect.width / containerRect.width) * 100;
      setLeftWidth(finalLeftWidth);
    }

    // 重新启用 transition
    setTimeout(() => {
      if (leftPanelRef.current) {
        leftPanelRef.current.classList.remove('no-transition');
      }
      if (rightPanelRef.current) {
        rightPanelRef.current.classList.remove('no-transition');
      }
    }, 50);
  }, [isDragging]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      // 清理动画帧
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // 新增：处理导出菜单的显示/隐藏
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setIsExportMenuVisible(false);
      }
    };

    // 根据菜单显示状态添加或移除事件监听器
    if (isExportMenuVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // 清理函数：移除事件监听器（无论菜单是否显示）
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };

  }, [isExportMenuVisible]);

  const toggleLeftPanel = () => {
    setIsLeftCollapsed(!isLeftCollapsed);
  };

  const toggleRightPanel = () => {
    setIsRightCollapsed(!isRightCollapsed);
  };

  // 新增：导出功能处理函数
  const handleExportExcel = () => {
    if (!hasData) return; // 无数据时不执行
    leftPanelExportActions?.onExportExcel?.();
    setIsExportMenuVisible(false);
  };

  const handleExportCsv = () => {
    if (!hasData) return; // 无数据时不执行
    leftPanelExportActions?.onExportCsv?.();
    setIsExportMenuVisible(false);
  };

  // 鼠标悬停处理 - 无数据时不显示菜单
  const handleMouseEnter = () => {
    if (hasData) {
      setIsExportMenuVisible(true);
    }
  };

  const handleMouseLeave = () => {
    setIsExportMenuVisible(false);
  };

  return (
    <div className="resizable-panels-container" ref={containerRef}>
      {/* 左侧面板 */}
      <div 
        ref={leftPanelRef}
        className={`resizable-panel-left ${isLeftCollapsed ? 'resizable-panel-collapsed' : ''}`}
        style={{ 
          width: isLeftCollapsed ? '60px' : `${leftWidth}%`,
          flex: isLeftCollapsed ? '0 0 60px' : `0 0 ${leftWidth}%`
        }}
      >
        <div className="resizable-panel-header">
          <h3 className="resizable-panel-title">
            <span className="resizable-panel-title-icon">📊</span>
            {!isLeftCollapsed && '数据表格'}
          </h3>
          {/* 新增：导出按钮区域 */}
          {!isLeftCollapsed && leftPanelExportActions && (
            <div className="resizable-panel-actions">
              <div 
                className={`export-button-container ${!hasData ? 'export-disabled' : ''}`}
                ref={exportMenuRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <button 
                  className={`export-main-button ${!hasData ? 'disabled' : ''}`}
                  title={!hasData ? `暂无数据可导出` : `导出数据 (共${dataCount}条)`}
                  disabled={!hasData}
                >
                  <span className="export-icon">📤</span>
                  导出
                  <span className="export-arrow">▼</span>
                </button>
                
                {isExportMenuVisible && hasData && (
                  <div className="export-dropdown-menu">
                    <button 
                      className="export-menu-item"
                      onClick={handleExportExcel}
                    >
                      <span className="export-menu-icon">📊</span>
                      导出Excel
                    </button>
                    <button 
                      className="export-menu-item"
                      onClick={handleExportCsv}
                    >
                      <span className="export-menu-icon">📄</span>
                      导出CSV
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          {/* <div className="resizable-panel-actions">
            <button 
              className="resizable-panel-action-btn"
              onClick={toggleLeftPanel}
              title={isLeftCollapsed ? '展开面板' : '折叠面板'}
            >
              {isLeftCollapsed ? '→' : '←'}
            </button>
          </div> */}
        </div>
        <div className="resizable-panel-content">
          {leftPanel}
        </div>
      </div>

      {/* 分隔条 */}
      {!isLeftCollapsed && !isRightCollapsed && (
        <div 
          ref={dividerRef}
          className={`resizable-divider ${isDragging ? 'dragging' : ''}`}
          onMouseDown={handleMouseDown}
        />
      )}

      {/* 右侧面板 */}
      <div 
        ref={rightPanelRef}
        className={`resizable-panel-right ${isRightCollapsed ? 'resizable-panel-collapsed' : ''}`}
        style={{ 
          width: isRightCollapsed ? '60px' : `${100 - leftWidth}%`,
          flex: isRightCollapsed ? '0 0 60px' : `0 0 ${100 - leftWidth}%`
        }}
      >
        <div className="resizable-panel-header">
          <h3 className="resizable-panel-title">
            {/* <span className="resizable-panel-title-icon">🤖</span> */}
            <div style={{width: '30px', height: '30px'}}>
              <img src="/static/media/ai.png" alt="AI Assistant" style={{width: '100%', height: '100%'}} />
            </div>
            {!isRightCollapsed && 'AI助手'}
          </h3>
          {/* <div className="resizable-panel-actions">
            <button 
              className="resizable-panel-action-btn"
              onClick={toggleRightPanel}
              title={isRightCollapsed ? '展开面板' : '折叠面板'}
            >
              {isRightCollapsed ? '←' : '→'}
            </button>
          </div> */}
        </div>
        <div className="resizable-panel-content">
          {rightPanel}
        </div>
      </div>
    </div>
  );
};

export default ResizablePanels;